import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Edit, X, ExternalLink, Sparkles, Wand2 } from "lucide-react";

export default function AdminLandings() {
  const [landings, setLandings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [bulk, setBulk] = useState({ running: false, total: 0, done: 0 });
  const load = () => adminApi.get("/admin/landings").then((r) => setLandings(r.data));
  useEffect(() => { load(); adminApi.get("/admin/landings/ai-status").then((r) => setBulk(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!bulk.running) return;
    const t = setInterval(async () => {
      try {
        const r = await adminApi.get("/admin/landings/ai-status");
        setBulk(r.data);
        if (!r.data.running) { toast.success("Contenido de todas las landings generado con IA"); load(); }
      } catch { /* noop */ }
    }, 3000);
    return () => clearInterval(t);
  }, [bulk.running]);

  const startAll = async () => {
    if (!window.confirm("¿Generar contenido único con IA para TODAS las landings? Puede tardar varios minutos y sustituirá el texto actual.")) return;
    try { const r = await adminApi.post("/admin/landings/ai-enrich-all"); setBulk({ ...r.data, running: true }); toast.success("Generación iniciada en segundo plano"); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo iniciar"); }
  };

  const save = async () => {
    await adminApi.put(`/admin/landings/${editing.id}`, editing);
    toast.success("Landing guardada");
    setEditing(null);
    load();
  };

  const enrich = async () => {
    setAiLoading(true);
    try {
      const r = await adminApi.post(`/admin/landings/${editing.id}/ai-enrich`);
      setEditing(r.data);
      toast.success("Contenido único generado con IA");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar con IA"); }
    finally { setAiLoading(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tighter mb-2">Landings SEO — Cataluña</h1>
          <p className="text-muted-foreground text-sm">Páginas geolocalizadas para posicionamiento por población.</p>
        </div>
        <button onClick={startAll} disabled={bulk.running} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-4 py-2 text-sm font-semibold hover:bg-brand-dark disabled:opacity-60" data-testid="ai-all-btn">
          <Wand2 className="h-4 w-4" /> {bulk.running ? `Generando ${bulk.done}/${bulk.total}…` : "Generar todas con IA"}
        </button>
      </div>
      {bulk.running && (
        <div className="mb-4 bg-white border border-border rounded-sm p-3" data-testid="ai-all-progress">
          <div className="flex items-center justify-between text-xs mb-1"><span>Generando contenido único con IA…</span><span>{bulk.done}/{bulk.total}</span></div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-brand transition-all" style={{ width: `${bulk.total ? (bulk.done / bulk.total) * 100 : 0}%` }} /></div>
        </div>
      )}
      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white"><tr><th className="text-left px-3 py-2">Población</th><th className="text-left px-3 py-2">Slug</th><th className="text-center px-3 py-2">Estado</th><th className="text-right px-3 py-2"></th></tr></thead>
          <tbody>
            {landings.map((l, i) => (
              <tr key={l.id} className={i % 2 ? "bg-secondary/40" : "bg-white"} data-testid={`landing-row-${l.slug}`}>
                <td className="px-3 py-2">{l.town}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">/{l.slug}</td>
                <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-sm ${l.published ? "bg-emerald-100 text-emerald-700" : "bg-secondary"}`}>{l.published ? "Publicada" : "Oculta"}</span></td>
                <td className="px-3 py-2 text-right">
                  <a href={`/${l.slug}`} target="_blank" rel="noreferrer" className="p-1.5 inline-block hover:text-brand"><ExternalLink className="h-4 w-4" /></a>
                  <button onClick={() => setEditing(l)} className="p-1.5 hover:text-brand"><Edit className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-sm w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-heading font-semibold text-lg">Editar landing · {editing.town}</h2><button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button></div>
            <label className="text-sm font-medium block">Título SEO</label>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" />
            <label className="text-sm font-medium block">Meta description</label>
            <textarea rows={2} value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" />
            <label className="text-sm font-medium block">Keywords (separadas por comas)</label>
            <input value={editing.keywords || ""} onChange={(e) => setEditing({ ...editing, keywords: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="landing-keywords" />
            <label className="text-sm font-medium block">Introducción</label>
            <textarea rows={2} value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" />
            <label className="text-sm font-medium block">Contenido (HTML)</label>
            <textarea rows={6} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm font-mono text-xs" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Publicada</label>
            <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2">
              <p className="text-sm font-medium">Banner promocional de esta población</p>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.promo?.enabled || false} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, enabled: e.target.checked } })} data-testid="landing-promo-enabled" /> Mostrar banner propio en esta landing</label>
              <input placeholder="Texto del banner" value={editing.promo?.text || ""} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, text: e.target.value } })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="landing-promo-text" />
              <input placeholder="Enlace (opcional)" value={editing.promo?.link || ""} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, link: e.target.value } })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" />
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-muted-foreground flex items-center gap-2">Color <input type="color" value={editing.promo?.bg || "#c2410c"} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, bg: e.target.value } })} className="h-8 w-10 rounded-sm border border-border cursor-pointer" /></label>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={editing.promo?.countdown || false} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, countdown: e.target.checked } })} /> Cuenta atrás</label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={editing.promo?.start || ""} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, start: e.target.value } })} className="px-3 py-2 text-sm border border-border rounded-sm" />
                <input type="date" value={editing.promo?.end || ""} onChange={(e) => setEditing({ ...editing, promo: { ...editing.promo, end: e.target.value } })} className="px-3 py-2 text-sm border border-border rounded-sm" />
              </div>
            </div>
            <button onClick={enrich} disabled={aiLoading} className="w-full border border-brand text-brand py-2.5 rounded-sm font-semibold hover:bg-brand/5 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="ai-landing-btn"><Sparkles className="h-4 w-4" /> {aiLoading ? "Generando contenido único…" : "Generar contenido único con IA"}</button>
            <button onClick={save} className="w-full bg-brand text-white py-2.5 rounded-sm font-semibold hover:bg-brand-dark" data-testid="save-landing-btn">Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
